import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDrawer } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AccessibilityHelper } from '../../../shared/accessibility.helper';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { ButtonConfig } from '../../../shared/components/list-view/list-view-button-config.model';
import { TrashDeleteEvent } from '../../../shared/components/list-view/list-view-trash-config.model';
import { ScriptableBaseComponent } from '../../../shared/components/scriptable-base/scriptable-base.component';
import { AlertDialogComponent, AlertDialogModel } from '../../../shared/dialogs/alert/alert-dialog.component';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../../shared/dialogs/confirmation/confirmation-dialog.component';
import { DisplayContentData, DisplayContentDialog, DisplayContentDialogInitData } from '../../../shared/dialogs/display-content/display-content.dialog';
import { ImportDragDropComponent } from '../../../shared/dialogs/import-drag-drop/import-drag-drop.component';
import { ImportFormDefinitionDialog } from '../../../shared/dialogs/import-form-definition/import-form-definition.dialog';
import { ImportStructureInstancesDialog } from '../../../shared/dialogs/import-structure/import-structure.dialog';
import { ConfirmationDialogEnums } from '../../../shared/enums/confirmation-dialog.enum';
import { DragDropEnum } from '../../../shared/enums/drag-drop.enum';
import { ItemTypeEnum } from '../../../shared/enums/item-type.enum';
import { IContextMenuItem } from '../../../shared/interfaces/icontext-menu-item.interface';
import { IListItem } from '../../../shared/interfaces/ilist-item.interface';
import { FormErrorMessages } from '../../../shared/models/error-messages/form-error-messages';
import { Form } from '../../../shared/models/form-builder/form.model';
import { FormsItemContextMenuData } from '../../../shared/models/forms/item-context-menu';
import { Folder } from '../../../shared/models/site-content/folder.model';
import { CommunicationService } from '../../../shared/services/communication.service';
import { CurrentSiteService } from '../../../shared/services/current-site.service';
import { DataCollectionService } from '../../../shared/services/data-collection.service';
import { DeleteOnConfirmDialogService } from '../../../shared/services/delete-on-confirm-dialog.service';
import { FormBuilderService } from '../../../shared/services/form-builder.service';
import { FormInstanceService } from '../../../shared/services/form-instance.service';
import { UtilityHelper } from '../../../shared/utility.helper';
import { FilesDroppedOnStructureEvent } from '../structure/structure.file-drop.event';

// Constants.
const NEW_FORM: string = 'new form';
const IMPORT_FORM: string = 'import form';
const EDIT_PROPERTIES: string = 'edit_properties';

@Component({
    selector: 'app-forms',
    templateUrl: './forms.component.html',
    styleUrls: ['./forms.component.scss'],
    standalone: false
})
export class FormsComponent extends ScriptableBaseComponent implements OnInit {
    // Properties.
    private formCatalog: Form[];
    private rootFolder: Folder;
    private buttonConfig: any;
    private showEditPropertiesDrawer: boolean = false;
    private selectedForm: Form = null;
    private selectedFormOriginalState: Form = null;

    // 04-15-2021:  added the following three properties.
    private contextMenuPosition = { x: '0px', y: '0px' };
    @ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger;
    @ViewChild('drawer') drawer: MatDrawer;

    private itemContextMenuData: FormsItemContextMenuData = null;
    @ViewChild('itemContextMenuComponent') itemContextMenuComponent: ContextMenuComponent;

    // Constructor.
    public constructor(
        private formBuilderService: FormBuilderService,
        private formInstanceService: FormInstanceService,
        private dataCollectionService: DataCollectionService,
        private currentSiteService: CurrentSiteService,
        private activeRoute: ActivatedRoute,
        private route: ActivatedRoute,
        protected router: Router,
        public dialog: MatDialog,
        protected communicationService: CommunicationService,
        private deleteOnConfirmDialogService: DeleteOnConfirmDialogService,
        private readonly titleService: Title,
        elForBaseClass: ElementRef) {
        super(elForBaseClass);
        let toolbarNamesMap = {
            'trash': 'Delete',
            'sort_by_alpha': 'Sort',
            'view_module': 'View Mode'
        };
        this.setupSearchForListViewElements(toolbarNamesMap, { 'library_books': 'Form Templates' });

        this.activeRoute.paramMap.subscribe(paramMap => { });

        this.router.events.subscribe((e: any) => {
            if (e instanceof NavigationEnd) { }
        });

        this.itemContextMenuData = new FormsItemContextMenuData(this);
    }

    // Life cycle methods.
    public ngOnInit() {
        super.ngOnInit();

        this.rootFolder = this.site.folders[0];

        //TEAMS-653: KLW - Seperate the instantiation of the ButtonConfig objects so the cssClass property can be set
        // SWH 02-24-2025 note re/updates for VNEXT-1619:  if the user is an admin, then show the new form template and import buttons; otherwise, don't.
        var newFormButton = new ButtonConfig('New Form Template', NEW_FORM);
        var importFormButton = new ButtonConfig('Import Form Template(s)', IMPORT_FORM);

        newFormButton.cssClass = 'button-right-margin';
        importFormButton.cssClass = 'button-right-margin';

        let rightButtons: any[] = this.site.siteIsAdministerable ? [newFormButton, importFormButton] : [];

        this.buttonConfig = {
            right: rightButtons, //[ newFormButton, importFormButton ],
            item_actions: [
                new ButtonConfig('View Details', EDIT_PROPERTIES, 'info')
            ]
        }
        if (this.buttonConfig?.right[0]) {
            this.buttonConfig.right[0].disabled = !this.site.siteIsBuildable;
        }
        else {
            console.log('this.buttonConfig not defined');
        }

        this.dataCollectionService.getFormsBySite(this.site.id).then(response => {
            this.formCatalog = response;
        });

        this.titleService.setTitle(this.route.snapshot.data['title'] + ' for ' + this.site.name);
    }

    // Accessor methods called by my HTML code.
    public get ListItems(): IListItem[] {
        return (this.formCatalog);
    }

    public get ButtonConfig(): any {
        return (this.buttonConfig);
    }

    public get ContextMenuPositionX(): string {
        return (this.contextMenuPosition.x);
    }
    public get ContextMenuPositionY(): string {
        return (this.contextMenuPosition.y);
    }

    public get UserCanDelete(): boolean {
        return this.site.siteIsAdministerable;
    }

    public get TypeSortOrderConfig(): string[] {
        return [ItemTypeEnum.FORM];
    }

    public get ColumnsConfig(): string[] {
        return ['select', 'name', 'childCount', 'modifiedDate', 'modifiedBy'];
    }

    public get ShowEditPropertiesDrawer(): boolean {
        return this.showEditPropertiesDrawer;
    }

    public get SelectedForm(): Form {
        return this.selectedForm;
    }

    public get EditFormPropertiesMode(): string {
        return (this.currentSiteService.Site.siteIsAdministerable ? 'edit' : 'view');
    }

    public get DetailsDrawerTitle(): string {
        return 'Form Details';
    }

    public get ItemContextMenuData(): IContextMenuItem[] {
        return this.itemContextMenuData.ItemContextMenuData;
    }
    public get Dialog(): MatDialog {
        return this.dialog;
    }
    public get FormCatalog(): Form[] {
        return this.formCatalog;
    }
    // Note:  the following setter method is less than idea, but it does allow logic to be moved out of this component class.
    public set FormCatalog(value: Form[]) {
        this.formCatalog = value;
    }
    public get FormBuilderService(): FormBuilderService {
        return this.formBuilderService;
    }

    // Handle control events.
    public doOnButtonClick(eventData: any): void {
        if (eventData.button == NEW_FORM) {
            let url = `/site/${this.site.id}/design/0?folderId=${this.rootFolder.id}`;
            let eventType = AccessibilityHelper.deduceEventType(eventData.originalEvent);
            this.router.navigateByUrl(url, { state: { originalEventType: eventType } }).then(x => {
                this.communicationService.emitChangeToSkipNavLink(url);
            });
        } else if (eventData.button == IMPORT_FORM) {
            this.importForm(this.site.id);

        }
    }

    private importForm(siteID: number) {
        // Process an import form dialogue.
        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';
        dialogConfig.data = null; // Not presently used.

        const dialogRef = this.dialog.open(ImportFormDefinitionDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(importedFormDefCount => {
            this.dataCollectionService.getFormsBySite(siteID).then(response => {
                this.formCatalog = response;
            });
        });
    }


    //TEAMS-252: KLW - Add necessary events for drag and drop
    public blockDrop(ev: DragEvent) {
        UtilityHelper.blockDrop(ev);
    }

    public allowDrop(ev) {
        UtilityHelper.allowDrop(ev);
    }

    public onFileDroppedFillRemaining(event: DragEvent): void {

        let dialogData: FilesDroppedOnStructureEvent = new FilesDroppedOnStructureEvent(event.dataTransfer.files);
        dialogData.setToDropSiteId(this.site.getId());
        dialogData.setObjectType(DragDropEnum.FORM_TEMPLATE);
        dialogData.setReload(true);


        this.showDroppedDialog(dialogData);
    }

    public onFileDropped(event: FilesDroppedOnStructureEvent): void {
        let dialogData: FilesDroppedOnStructureEvent = new FilesDroppedOnStructureEvent(event.getFiles());
        dialogData.setToDropSiteId(this.site.getId());
        dialogData.setObjectType(DragDropEnum.FORM_TEMPLATE);
        dialogData.setReload(event.getReload());


        this.showDroppedDialog(dialogData);
    }

    private showDroppedDialog(config: FilesDroppedOnStructureEvent) {
        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';
        dialogConfig.data = config;

        const dialogRef = this.dialog.open(ImportDragDropComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(importedFormId => {
            if (((importedFormId != null) && (importedFormId > 0)) || config.getReload()) {
                this.dataCollectionService.getFormsBySite(this.site.id).then(response => {
                    this.formCatalog = response;
                });
            }
        });
    }

    public doOnEditProperties(eventData: any): void {
        this.selectedForm = eventData.item;
        this.selectedFormOriginalState = new Form();
        this.selectedFormOriginalState.name = this.selectedForm.name;
        this.selectedFormOriginalState.description = this.selectedForm.description;

        this.formBuilderService.get(this.selectedForm.id).then(fullFormDef => {
            // Save the full form def as the selected form.
            this.selectedForm = fullFormDef;

            // Open the properties drawer.
            this.showEditPropertiesDrawer = true;
            this.drawer.open();
        });
    }

    public doOnFormSave(eventData: any): void {
        this.closeDrawerClicked();

        // Does a form with this name already exist?
        let existingForm: Form = this.formCatalog.find(f => (f.name == this.selectedForm.name) && (f.id != this.selectedForm.id));
        if (existingForm == null) {
            this.formBuilderService.update(this.selectedForm).then(savedForm => {
                let formToUpdate: Form = this.formCatalog.find(f => f.id == savedForm.id);
                if (formToUpdate) {
                    formToUpdate.name = savedForm.name;
                    formToUpdate.description = savedForm.description;
                }
            });
        } else {
            let message: string = FormErrorMessages.duplicateFormInSiteMessage(this.selectedForm.name, this.site.id);
            this.dialog.open(AlertDialogComponent, {
                maxWidth: "600px",
                data: new AlertDialogModel("Cannot Save Form", message)
            });

            if ((this.selectedForm != null) && (this.selectedFormOriginalState != null)) {
                this.selectedForm.name = this.selectedFormOriginalState.name;
                this.selectedForm.description = this.selectedFormOriginalState.description;

                this.selectedFormOriginalState = null;
            }
        }
    }

    public doOnFormPropertiesClose(eventData: any): void {
        this.closeDrawerClicked();
    }

    public closeDrawerClicked(): void {
        this.showEditPropertiesDrawer = false;
        this.drawer.close();
    }

    public drawerClosing(): void {
        this.showEditPropertiesDrawer = false;
    }

    public doOnEdit(eventData: any): void {
        let id = eventData.item.id;
        let url = `/site/${this.site.id}/design/${id}`;
        let eventType = AccessibilityHelper.deduceEventType(eventData.originalEvent);
        this.router.navigateByUrl(url, { state: { originalEventType: eventType } }).then(x => {
            this.communicationService.emitChangeToSkipNavLink(url);
        });
    }

    public doOnItemAction(eventData: any): void {
        if (eventData.button.value == EDIT_PROPERTIES) {
            this.doOnEditProperties(eventData);
        } else if (eventData.button.value == 'edit') {
            this.doOnEdit(eventData);
        }
    }

    public doOnItemDelete(eventData: TrashDeleteEvent): void {
        let deletedFormItem: IListItem = eventData.items[0];

        this.formInstanceService.getFormInstancesUsingForm(deletedFormItem.getId()).then(fiCount => {
            if (fiCount > 0) {
                var message = "This form cannot be deleted because it is used by ";
                if (fiCount == 1) message += "a form.";
                if (fiCount > 1) message += fiCount + " forms."

                let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                    data: new ConfirmationDialogModel(`Template in Use`,
                        message, { hideNo: true, showOk: true, dialogType: ConfirmationDialogEnums.WARNING })
                });
            }
            else {
                this.deleteOnConfirmDialogService.deleteOnConfirm(this.dialog, "Form Template", () => {
                    this.formBuilderService.deleteFromUI(deletedFormItem.getId()).then(deletedFormId => {
                        this.formCatalog =
                            this.formCatalog.filter(f => f.getId() !== deletedFormId);
                    });
                });
            }
        });
    }

    // Handle context menu events.
    public doOnItemContextMenu(event: any): void {
        // Set the context menu position.
        let itemContextMenuPositionX = event.originalEvent.clientX + 'px';
        let itemContextMenuPositionY = event.originalEvent.clientY + 'px';
        if (this.itemContextMenuComponent != null)
            this.itemContextMenuComponent.openMenuForItemAt(event.item, itemContextMenuPositionX, itemContextMenuPositionY);

        return;
    }

    public doOnShowImportFormatExampleItemClick(eventData: any): void {
        // Request an example of the import format for the selected form.
        let contextMenuForm: Form = this.contextMenu.menuData.item;

        this.formBuilderService.getImportFormatExample(contextMenuForm.id).then(responseData => {
            let contentData: DisplayContentData[] =
                [
                    { content: responseData.xmlFormatExample, tabName: 'Xml Format', makeContentReadonly: true },
                    { content: responseData.jsonFormatExample, tabName: 'Json Format', makeContentReadonly: true }
                ];
            let initData: DisplayContentDialogInitData = new DisplayContentDialogInitData('Import Format Examples', contentData);

            let dialogConfig: MatDialogConfig = new MatDialogConfig();
            dialogConfig.hasBackdrop = true;
            dialogConfig.width = '500px';
            dialogConfig.height = '615px';
            dialogConfig.data = initData;

            const dialogRef = this.dialog.open(DisplayContentDialog, dialogConfig);
            dialogRef.afterClosed().subscribe(na => {
            });
        });
    }

    public doOnImportCopiesItemClick(eventData: any): void {
        // Process an import structure instances dialogue
        //
        // Note:  presently this dialogue only exports
        //        form instances.
        let contextMenuForm: Form = this.contextMenu.menuData.item;

        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';
        dialogConfig.data = contextMenuForm;

        const dialogRef = this.dialog.open(ImportStructureInstancesDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(targetFolderId => {
            // Note:  the export is performed in the dialog component class.
            if ((targetFolderId != null) && (targetFolderId > 0)) {
                const url: string = `/site/${this.currentSiteService.Site.id}/structure/folder/${targetFolderId}`;
                this.router.navigateByUrl(url, {});
            }
        });
    }

    // Private helper methods.
    protected get site() { // Not called by my HTML.
        return this.currentSiteService.Site;
    }
}
